<template>
  <div :class="$style.container">
    <div :class="$style.header">
      <div :class="[$style.row, $style.heading]">
        <slot name="woc-heading">
          <div :class="$style.slotHeading">
            <span :class="$style.title">{{ title }}</span>
            <span :class="$style.productCount">{{ productCountLabel }}</span>
          </div>
        </slot>
      </div>
      <div v-if="sortedSkus.length > 0" :class="[$style.row, $style.sort]">
        <div :class="$style.sortBy">{{ sortByLabel }}</div>
        <NsSlottedDropdown
          :class="$style.sortDropdown"
          :selected="sortByValue"
          :placeholder="selectPlaceholder"
          @select="sortSelect"
        >
          <div v-for="opt in sortOptions" :key="opt.id" :data-value="opt.id">
            <span>{{ opt.title }}</span>
          </div>
        </NsSlottedDropdown>
      </div>
    </div>

    <div v-if="showSpinnerOnFirstLoad" :class="$style.spinnerWrapper">
      <NsSpinner />
    </div>
    <NsProductList
      v-else
      :items="sortedSkus"
      :mobile-side-by-side="mobileSideBySide"
      :skus-per-load="skusPerLoad"
      @product-update="handleProductUpdate"
    />
  </div>
</template>

<script>
import { events } from "@nuskin/ns-util";
import { NsSlottedDropdown, NsSpinner } from "@nuskin/design-components";
import { fromJsonString, isNullOrEmpty } from "@nuskin/ns-common-lib";

import debounce from "lodash/debounce";

import NsProductList from "./NsProductList.vue";

const PRODUCT_COUNT = "%count%";
const DISTRIBUTOR_ONLY_SORT_OPTIONS = ["pointHighToLow", "pointLowToHigh"];

/**
 * NsProductListSortable
 * Events
 * @event sort-by-select  Fired when a dropdown item is selected with (id) => {}
 */

export default {
  name: "NsProductListSortable",
  components: { NsSlottedDropdown, NsSpinner, NsProductList },
  props: {
    /**
     * Determines if the product cards should be displayed side-by-side in mobile
     */
    mobileSideBySide: {
      type: Boolean,
      default: true
    },
    /**
     * 'Products' label for translations. Should include '%count%'. See default
     * for English example
     */
    productsLabel: {
      type: String,
      default: "%count% Products"
    },
    /**
     * The placehoder text in the sortBy dropdown
     */
    selectPlaceholder: {
      type: String,
      default: ""
    },
    /**
     * Array of skus for product list. Takes array of strings or objects
     * ([sku] || [{ sku }])
     */
    skus: {
      type: [String, Array],
      default: () => []
    },
    /**
     * Object containing the search scores for sorting
     */
    skuSearchScores: {
      type: Object,
      default: null
    },
    /**
     * SortBy label
     */
    sortByLabel: {
      type: String,
      default: ""
    },
    /**
     * SortBy options. Array of objects:  [{ id, title, sort, order }]
     */
    sortByOptions: {
      type: Array,
      default: () => []
    },
    /**
     * Set sortBy selected option programmically. Should match one of
     * sortByOptions[n].id
     */
    sortBySelected: {
      type: String,
      default: ""
    },
    /**
     * Title of list
     */
    title: {
      type: String,
      default: ""
    },
    /**
     * Initial number of products displayed and number added per "Load More" button click. Set to 0 to disable.
     */
    skusPerLoad: {
      type: Number,
      default: 20
    }
  },
  data: function() {
    return {
      products: {},
      localSku: [],
      sortedSkus: [],
      sortByValue: this.sortBySelected
    };
  },
  computed: {
    sortOptions() {
      return this.sortByOptions.filter(option => {
        // exclude distributor_only filter option if user != distributor
        if (
          DISTRIBUTOR_ONLY_SORT_OPTIONS.includes(option.id) &&
          !this.$NsProductUserService.isDistributor
        ) {
          return false;
        }
        // exclude sorting by best match unless scores provided
        return option.id !== "bestMatch" || !!this.skuSearchScores;
      });
    },
    showSpinnerOnFirstLoad() {
      return (
        this.sortedSkus.length <= this.skusPerLoad &&
        this.$NsProductDataService.batches.some(
          batch =>
            !["success", "failed"].includes(batch.status) &&
            batch.skus.some(batchSku => this.localSkus.includes(batchSku))
        )
      );
    },
    productCount() {
      const productsFound = this.$NsProductDataService.findProducts(
        this.sortedSkus
      );
      return productsFound.length;
    },
    productCountLabel() {
      return this.productsLabel.replace(PRODUCT_COUNT, this.productCount);
    }
  },
  watch: {
    skus: {
      immediate: true,
      handler(skus) {
        this.localSkus = fromJsonString(skus, []).map(item => {
          if (typeof item === "object") {
            return item.sku || item.SKU;
          }
          return item;
        });
        this.sortBy();
      }
    },
    sortBySelected(value) {
      this.sortByValue = value;
    },
    sortByValue() {
      this.sortBy();
    }
  },
  methods: {
    /**
     * Saves the products' data in local state, so it can sort by this data
     * @pram <Product> product
     */
    handleProductUpdate(product) {
      if (product) {
        this.products = {
          ...this.products,
          [product.sku]: { ...this.products[product.sku], ...product }
        };
      }
    },

    /**
     * Used to get the price type for the current user
     */
    getPriceType() {
      return this.$NsProductDataService.getPriceType();
    },

    /**
     * Used to get the price to sort a produce
     * @param product
     * @returns {number}
     * @private
     */
    getProductSortingPrice(product, priceType) {
      if (isNullOrEmpty(product)) {
        return 0;
      }

      product.setPriceAndPvFromType(priceType);

      const productPrice = product.isBase()
        ? product.getMinPrice()
        : product.getPrice(); // don't use .price here
      return productPrice;
    },

    /**
     * Used to get the points to sort a produce
     * @param product
     * @returns {number}
     * @private
     */
    getProductSortingPv(product, priceType) {
      if (isNullOrEmpty(product)) {
        return 0;
      }

      product.setPriceAndPvFromType(priceType);

      const productPv = product.isBase() ? product.getMinPv() : product.pv;
      return productPv;
    },

    sortSelect(value) {
      if (!this.$NsProductDataService.check(this.localSkus)) {
        this.$NsProductDataService.queue(this.localSkus);
      }

      this.sortByValue = value;
      this.$emit("sort-by-select", value);
      events.publish(events.shop.SEARCH_SORT_DROPDOWN);
    },

    sortBy: debounce(function() {
      // Find sortOption
      const sortOption = this.sortOptions.find(
        option => option.id === this.sortByValue
      );

      // if default or no sort, reset to current skus
      if (!sortOption || !sortOption.sort || sortOption.sort === "default") {
        this.sortedSkus = [...this.localSkus];
        return;
      }

      const sort = sortOption.sort;
      const order = sortOption.order;

      const products = this.$NsProductDataService
        .findProducts(this.localSkus, false)
        .reduce((a, p) => ({ ...a, [p.sku]: p }), {});

      const sortedSkus = [...this.localSkus].sort((x, y) => {
        //make sure to get sku
        //@note every product gets updated every product card load
        const xSku = typeof x === "object" ? x.sku : x;
        const ySku = typeof y === "object" ? y.sku : y;

        // get full product objects
        const a = products[xSku] || {};
        const b = products[ySku] || {};

        if (sort === "title") {
          return order === "desc"
            ? (b[sort] || "").localeCompare(a[sort] || "")
            : (a[sort] || "").localeCompare(b[sort] || "");
        } else if (sort === "price") {
          const priceType = this.getPriceType();
          return order === "desc"
            ? this.getProductSortingPrice(b, priceType) -
                this.getProductSortingPrice(a, priceType)
            : this.getProductSortingPrice(a, priceType) -
                this.getProductSortingPrice(b, priceType);
        } else if (sort === "pv") {
          const priceType = this.getPriceType();
          return order === "desc"
            ? this.getProductSortingPv(b, priceType) -
                this.getProductSortingPv(a, priceType)
            : this.getProductSortingPv(a, priceType) -
                this.getProductSortingPv(b, priceType);
        } else if (sort === "best") {
          //fallback condition - if product is not updated yet, use the search scores from NsSearchResults
          const aSearchScore =
            (this.skuSearchScores[a.sku]
              ? this.skuSearchScores[a.sku]
              : this.skuSearchScores[xSku]) || 0;
          const bSearchScore =
            (this.skuSearchScores[b.sku]
              ? this.skuSearchScores[b.sku]
              : this.skuSearchScores[ySku]) || 0;

          return bSearchScore - aSearchScore;
        } else {
          return order === "desc"
            ? sortValue(b[sort]) - sortValue(a[sort])
            : sortValue(a[sort]) - sortValue(b[sort]);
        }
      });
      this.sortedSkus = sortedSkus;

      function sortValue(item) {
        if (typeof item === "undefined" || item === null) {
          return 0;
        }

        return item;
      }
    }, 250)
  }
};
</script>

<style lang="scss" module>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";
@use "~@nuskin/ns-core-styles/src/scss/colors";

.container {
  margin: 0 auto;
  padding: 0 0 15px;
  max-width: 1335px;
  background-color: rgb(246, 247, 247);
  box-sizing: border-box;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  background-color: rgb(246, 247, 247);
  padding: 10px 15px;
  padding-bottom: 0px;
}

.row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;

  &.heading {
    margin: 5px 0 15px;
  }

  &.sort {
    justify-content: flex-end;
    align-items: center;
    flex-grow: 1;
    margin-left: 40px;
  }
}

.slotHeading {
  display: flex;
  align-items: baseline;
  line-height: normal;
}

.title {
  display: none;
  color: rgb(39, 37, 37);
  font-size: 22px;
  font-weight: 600;
}

.productCount {
  display: none;
  color: rgb(99, 102, 106);
  font-size: 14px;
  font-weight: normal;
}

.sortBy {
  margin-right: 10px;
  color: rgb(99, 102, 106);
  font-size: 14px;
  font-weight: normal;
  line-height: normal;
}

.sort-selector {
  padding: 7px 10px;
  border: 1px solid rgb(209, 209, 209);
  border-radius: 2px;
  width: 160px;
  background: #ffffff;
  font-size: 16px;
  line-height: normal;
  text-align: left;
}

.sortDropdown {
  width: 210px !important;
}

.spinnerWrapper {
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 10px;
}

@media (min-width: 768px) {
  .container {
    padding: 25px 15px;
  }

  .header {
    flex-wrap: nowrap;
    padding: 0;
  }

  .slotHeading {
    margin-left: 15px;
  }

  .title {
    display: block;
  }

  .productCount {
    display: block;
    margin-left: 10px;
  }

  .row {
    justify-content: flex-start;

    &.heading {
      margin-bottom: 20px;
    }

    &.sort {
      flex-grow: 0;
      margin: 0;
      margin-bottom: 15px;
    }
  }

  .sortBy {
    color: rgb(187, 188, 188);
    font-size: 16px;
  }

  .sort-selector {
    width: 210px;
  }
}
</style>
